import useSocketReconnect from '../../utils/useSocketReconnect';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { COLLA } from '../../utils/utils';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './BestSellers.css';
import { getSalesLines } from './merxandatgeUtils';
import { formatMoney } from '../../utils/money';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

function BestSellers({ socket, itemImages = {}, deletedItemIds = new Set(), items = [], deletedItems = [] }) {
    useSocketReconnect(socket, () => {
        socket.emit('.get_all_merxandatge_orders');
    });

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bestSellersFilter, setBestSellersFilter] = useState('current');
  const [localItemImages, setLocalItemImages] = useState({});
  const requestedImages = useRef(new Set());
  const [showStock, setShowStock] = useState(false);

  useEffect(() => {
    socket.emit('.get_all_merxandatge_orders');

    const handleAllOrders = (data) => {
      setOrders(data);
      setLoading(false);

      // Request images for each unique item
      if (data && data.length > 0) {
        const itemIds = new Set();
        data.forEach(order => {
          getSalesLines(order.items).forEach(item => {
            if (item.itemId) itemIds.add(item.itemId);
          });
        });
        itemIds.forEach(itemId => {
          // Once per item: every orders reply (also MerxandatgeOrders' reloads) asked again
          if (!itemImages[itemId] && !requestedImages.current.has(itemId)) {
            requestedImages.current.add(itemId);
            socket.emit('.get_merxandatge_image', itemId);
          }
        });
      }
    };

    const handleItemImageUrl = (data) => {
      if (data.itemId && data.url) {
        setLocalItemImages(prev => ({
          ...prev,
          [data.itemId]: data.url
        }));
      }
    };

    socket.on('.all_merxandatge_orders', handleAllOrders);
    socket.on('.merxandatge_image_url', handleItemImageUrl);

    return () => {
      socket.off('.all_merxandatge_orders', handleAllOrders);
      socket.off('.merxandatge_image_url', handleItemImageUrl);
    };
  }, [socket]);

  // Get season start/end dates helper
  const getSeasonDates = (year) => {
    if (COLLA === 'sarri') {
      return {
        start: moment(`${year}-01-01`),
        end: moment(`${year}-12-31`).endOf('day')
      };
    }
    return {
      start: moment(`${year}-09-01`),
      end: moment(`${year + 1}-08-31`).endOf('day')
    };
  };

  // Get current season year
  const getCurrentSeasonYear = () => {
    const now = moment();
    if (COLLA === 'sarri') {
      return now.year();
    }
    return now.month() >= 8 ? now.year() : now.year() - 1;
  };

  const bestSellers = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    // Filter orders by date based on selected filter
    const now = moment();
    let filteredOrders = orders;

    if (bestSellersFilter === '30days') {
      const cutoff = now.clone().subtract(30, 'days');
      filteredOrders = orders.filter(o => moment(o.created_at).isAfter(cutoff));
    } else if (bestSellersFilter === '3months') {
      const cutoff = now.clone().subtract(3, 'months');
      filteredOrders = orders.filter(o => moment(o.created_at).isAfter(cutoff));
    } else if (bestSellersFilter === 'current') {
      const currentYear = getCurrentSeasonYear();
      const { start, end } = getSeasonDates(currentYear);
      filteredOrders = orders.filter(o => {
        const orderDate = moment(o.created_at);
        return orderDate.isBetween(start, end, null, '[]');
      });
    } else if (bestSellersFilter === 'previous') {
      const previousYear = getCurrentSeasonYear() - 1;
      const { start, end } = getSeasonDates(previousYear);
      filteredOrders = orders.filter(o => {
        const orderDate = moment(o.created_at);
        return orderDate.isBetween(start, end, null, '[]');
      });
    }

    const aggregated = {};

    // Regex to match adult sizes: S, M, L with optional X prefix (XS, XXS, XXXS, XL, XXL, etc.)
    // or numeric prefix (2XS, 3XL, 4XL, 5XL, etc.)
    const adultSizeRegex = /^(X+|[2-9]X)?(S|M|L)$/i;

    // Get numeric sort value for adult sizes (smaller = earlier in list)
    const getAdultSizeOrder = (size) => {
      const upper = (size || '').toUpperCase();
      const match = upper.match(adultSizeRegex);
      if (!match) return null;

      const prefix = match[1] || '';
      const base = match[2];

      // Base values: S=0, M=50, L=100
      let value = base === 'S' ? 0 : base === 'M' ? 50 : 100;

      if (prefix) {
        // Count X's or parse numeric prefix
        const xCount = prefix.startsWith('X') ? prefix.length : parseInt(prefix[0], 10);
        if (base === 'S') {
          // XS, XXS, XXXS → smaller values (go before S)
          value = -xCount;
        } else if (base === 'L') {
          // XL, XXL, XXXL, 4XL, 5XL → larger values (go after L)
          value = 100 + xCount;
        }
      }
      return value;
    };

    const sortSizes = (sizes) => {
      if (!sizes || sizes.length === 0) return [];
      return [...sizes].sort((a, b) => {
        const orderA = getAdultSizeOrder(a);
        const orderB = getAdultSizeOrder(b);
        // Both are recognized adult sizes → sort by computed order
        if (orderA !== null && orderB !== null) return orderA - orderB;
        // Only A is adult → B (children/unknown) comes first
        if (orderA !== null) return 1;
        // Only B is adult → A (children/unknown) comes first
        if (orderB !== null) return -1;
        // Both unknown (children sizes) → sort alphabetically
        return (a || '').localeCompare(b || '');
      });
    };

    filteredOrders.filter(order => order.status !== 'cancelled' && !(order.status === 'pending' && order.stripe_payment_id)).forEach(order => {
      getSalesLines(order.items).forEach(item => {
        const key = item?.itemId || item?.name || 'desconegut';
        const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
        const price = typeof item.price === 'number' ? item.price : 0;
        const size = (item?.variant?.size || item?.size || '').trim();

        if (!aggregated[key]) {
          aggregated[key] = {
            key,
            itemId: item?.itemId,
            name: item?.name || 'Article desconegut',
            quantity: 0,
            revenue: 0,
            sizeCounts: {}
          };
        }

        aggregated[key].quantity += quantity;
        aggregated[key].revenue += price * quantity;

        if (size) {
          aggregated[key].sizeCounts[size] = (aggregated[key].sizeCounts[size] || 0) + quantity;
        }
      });
    });

    return Object.values(aggregated)
      .map(entry => {
        const sortedSizes = sortSizes(Object.keys(entry.sizeCounts));
        const sortedSizeCounts = {};
        sortedSizes.forEach(sizeKey => {
          sortedSizeCounts[sizeKey] = entry.sizeCounts[sizeKey];
        });

        return {
          ...entry,
          sizeCounts: sortedSizeCounts
        };
      })
      .filter(entry => entry.quantity > 0)
      .filter(entry => !entry.itemId || !deletedItemIds.has(entry.itemId))
      .sort((a, b) => {
        if (b.quantity !== a.quantity) return b.quantity - a.quantity;
        return b.revenue - a.revenue;
      });
  }, [orders, bestSellersFilter, deletedItemIds]);

  // Set of made_to_order item IDs
  const madeToOrderIds = useMemo(() => {
    const set = new Set();
    [...items, ...deletedItems].forEach(item => {
      if (item.id && item.type === 'made_to_order') set.add(item.id);
    });
    return set;
  }, [items, deletedItems]);

  // Build a map: itemId → { size → totalStock } aggregating across colors
  const stockByItemSize = useMemo(() => {
    const map = {};
    const allItems = [...items, ...deletedItems];
    allItems.forEach(item => {
      if (!item.id || !item.variants || item.type === 'made_to_order') return;
      const sizeMap = {};
      item.variants.forEach(v => {
        const size = (v.size || '').trim();
        if (size) {
          sizeMap[size] = (sizeMap[size] || 0) + (v.stock || 0);
        }
      });
      map[item.id] = sizeMap;
    });
    return map;
  }, [items, deletedItems]);

  const getItemImageUrl = (itemId) => {
    return itemImages[itemId] || localItemImages[itemId] || '/default-merchandise.png';
  };

  const renderSizeChart = (sizeCounts, itemId) => {
    const labels = Object.keys(sizeCounts || {});
    if (labels.length === 0) return null;

    const datasets = [
      {
        label: 'Venudes',
        data: labels.map(label => sizeCounts[label] || 0),
        backgroundColor: '#4c7cf4',
        borderRadius: 6,
        datalabels: {
          color: '#ffffff',
        }
      }
    ];

    if (showStock && itemId && stockByItemSize[itemId]) {
      const sizeStock = stockByItemSize[itemId];
      datasets.push({
        label: 'Estoc actual',
        data: labels.map(label => sizeStock[label] || 0),
        backgroundColor: '#c4c4c4',
        borderRadius: 6,
        datalabels: {
          display: true,
          color: (context) => context.dataset.data[context.dataIndex] === 0 ? '#e53935' : '#333333',
          anchor: (context) => context.dataset.data[context.dataIndex] === 0 ? 'end' : 'center',
          align: (context) => context.dataset.data[context.dataIndex] === 0 ? 'top' : 'center',
        }
      });
    }

    const data = { labels, datasets };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: showStock
        },
        tooltip: {
          callbacks: {
            label: (context) => `${context.dataset.label}: ${context.parsed.y}`
          }
        },
        datalabels: {
          anchor: 'center',
          align: 'center',
          font: {
            weight: 'bold'
          },
          display: (context) => context.dataset.data[context.dataIndex] > 0
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0
          }
        }
      }
    };

    return (
      <div className="size-chart-container">
        <div className="size-chart-title">Talles venudes</div>
        <div className="size-chart-wrapper">
          <Bar data={data} options={options} />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="best-sellers-section">
        <h2>Best-sellers</h2>
        <p>Carregant...</p>
      </div>
    );
  }

  return (
    <div className="best-sellers-section">
      <div className="best-sellers-header">
        <h2>Best-sellers</h2>
        <label className="best-sellers-stock-toggle">
          <input
            type="checkbox"
            checked={showStock}
            onChange={(e) => setShowStock(e.target.checked)}
          />
          Mostrar estocs
        </label>
        <select
          className="best-sellers-filter"
          value={bestSellersFilter}
          onChange={(e) => setBestSellersFilter(e.target.value)}
        >
          <option value="30days">Últims 30 dies</option>
          <option value="3months">Últims 3 mesos</option>
          <option value="current">Aquesta temporada</option>
          <option value="previous">Temporada anterior</option>
          <option value="all">Totes les temporades</option>
        </select>
      </div>
      {bestSellers.length === 0 ? (
        <p className="no-best-sellers">Encara no hi ha dades per mostrar els articles més venuts.</p>
      ) : (
        <div className="best-sellers-scroll">
          {bestSellers.map((item, index) => (
            <div className="best-seller-card" key={item.key}>
              <div className="best-seller-rank">#{index + 1}</div>
              <div className="best-seller-top">
                <div className="best-seller-image-wrapper">
                  <img
                    src={getItemImageUrl(item.itemId)}
                    alt={item.name}
                    className="best-seller-image"
                    onError={(e) => {
                      e.target.src = '/default-merchandise.png';
                    }}
                  />
                </div>
                <div className="best-seller-info">
                  <div className="best-seller-title">
                    {item.name}
                    {item.itemId && madeToOrderIds.has(item.itemId) ? <span className="made-to-order-tag">Sota comanda</span> : null}
                    {item.itemId && deletedItemIds.has(item.itemId) ? <span className="archived-tag"> (Arxivat)</span> : null}
                  </div>
                  <div className="best-seller-metrics">
                    <span><strong>{item.quantity}</strong> unitats venudes</span>
                    <span><strong>{formatMoney(item.revenue)}</strong> € ingressats</span>
                  </div>
                </div>
              </div>
              {renderSizeChart(item.sizeCounts, item.itemId)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BestSellers;

